import { useState } from 'react'
import { Link } from 'react-router'
import { LogOut, ChevronUp, KeyRound, LayoutDashboard } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { resolveStudentMascotDisplay } from '../lib/student-display'
import { StudentLoginSettingsDialog, STUDENT_LOGIN_SETTINGS_LABEL } from './StudentLoginSettingsDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'

export function shouldShowMemberRole(memberRole: string | null): boolean {
  return Boolean(memberRole && memberRole !== 'STUDENT')
}

export function shouldShowStudentLoginSettings(memberRole: string | null): boolean {
  return memberRole === 'STUDENT'
}

export function SidebarFooter() {
  const { user, logout } = useAuth()
  const { memberRole, memberName, memberUsername } = useWorkspace()
  const [isStudentLoginSettingsOpen, setIsStudentLoginSettingsOpen] = useState(false)
  const canOpenManagement = memberRole === 'ADMIN' || memberRole === 'TEACHER'
  const canOpenStudentLoginSettings = shouldShowStudentLoginSettings(memberRole)
  const managementHref = '/admin/usage'

  const resolvedName = memberName || user?.email || 'User'
  const resolvedAvatar = user?.picture ?? user?.avatar
  const userInitial = resolvedName.charAt(0).toUpperCase()
  const studentMascot = resolveStudentMascotDisplay({
    username: memberUsername ?? undefined,
  })
  const showRole = shouldShowMemberRole(memberRole)

  return (
    <div className="shrink-0 p-3 border-t-2 border-nb-border">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="w-full flex items-center justify-between px-1 py-1 rounded-base transition-colors hover:bg-main/10 data-[state=open]:bg-main/10">
            <div className="flex items-center gap-2">
              {resolvedAvatar ? (
                <img
                  src={resolvedAvatar}
                  alt={resolvedName}
                  className="w-7 h-7 rounded-full border-2 border-nb-border object-cover"
                />
              ) : studentMascot.emoji ? (
                <div
                  className="w-7 h-7 rounded-full border-2 border-nb-border bg-main flex items-center justify-center text-sm"
                  title={studentMascot.label ?? 'Student mascot'}
                >
                  {studentMascot.emoji}
                </div>
              ) : (
                <div className="w-7 h-7 rounded-full border-2 border-nb-border bg-main flex items-center justify-center text-[11px] font-heading text-main-foreground">
                  {userInitial}
                </div>
              )}
              <span className="text-sm text-foreground/80 font-base truncate max-w-[120px]">
                {resolvedName}
              </span>
            </div>
            <ChevronUp className="h-4 w-4 text-foreground/40" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="top" align="start" className="mb-1 w-52 bg-background text-foreground">
          <DropdownMenuLabel className="px-3 py-3">
            <div className="flex min-w-0 flex-col gap-1">
              <div className="truncate text-base font-heading text-foreground">{resolvedName}</div>
              {showRole ? (
                <div>
                  <span className="inline-flex rounded-base border-2 border-nb-border bg-secondary-bg px-2 py-0.5 text-[11px] font-heading text-foreground">
                    {memberRole}
                  </span>
                </div>
              ) : null}
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          {canOpenStudentLoginSettings ? (
            <DropdownMenuItem
              className="bg-background text-foreground focus:bg-secondary-bg"
              onSelect={() => setIsStudentLoginSettingsOpen(true)}
            >
              <KeyRound className="h-4 w-4" />
              <span>{STUDENT_LOGIN_SETTINGS_LABEL}</span>
            </DropdownMenuItem>
          ) : null}

          {canOpenManagement && (
            <DropdownMenuItem asChild className="bg-background text-foreground focus:bg-secondary-bg">
              <Link to={managementHref} className="flex items-center gap-2 w-full">
                <LayoutDashboard className="h-4 w-4" />
                <span>Admin Panel</span>
              </Link>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="bg-background text-destructive focus:bg-secondary-bg focus:text-destructive focus:border-destructive/50"
            onClick={() => void logout()}
          >
            <LogOut className="h-4 w-4" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {canOpenStudentLoginSettings ? (
        <StudentLoginSettingsDialog
          open={isStudentLoginSettingsOpen}
          onOpenChange={setIsStudentLoginSettingsOpen}
          studentName={resolvedName}
          studentUsername={memberUsername}
        />
      ) : null}
    </div>
  )
}
